import { useState, useEffect, useRef, useCallback } from 'react';
import Editable from '../Editable';
import ImageDrop from '../ImageDrop';
import { useSection } from '../useSection';
import { useDragReorder } from '../useDragReorder';
import { authFetch } from '../../auth';
import { ContactFormPreview } from '../FormPreview';

const API = '/api/cms/solutions';

// TB_PRODUCT_CATALOG를 사용하는 슬러그 — 버튼 레이블 "제품 카탈로그 수정 →" 표시용
const CUSTOM_PROD_SLUGS = new Set(['rugged', 'military', 'industrial', 'railway', 'sensor']);

/**
 * 응용 분야 — DB(/api/cms/solutions)에서 한국어로 불러와 편집, 저장 시 POST(upsert).
 * 카드 텍스트(분야명/부제/설명) + 상단 헤더·견적 문구(solutions/ui 섹션)가 DB 연동 대상.
 */
export default function Solutions({ onOpenProduct, onRegisterSave, onRegisterDirty, refreshKey = 0 }) {
  const [items, setItems] = useState(null);
  const itemsRef = useRef([]);
  const delRef = useRef([]);      // 저장 시 삭제할 slug(삭제 예약)
  const dirtyRef = useRef(false);
  const { data: ui, setField, saveSection, isDirty: uiIsDirty } = useSection('solutions', 'ui', 1);

  useEffect(() => {
    let alive = true;
    authFetch(`${API}?lang=ko`)
      .then((r) => r.json())
      .then((d) => { if (alive) { itemsRef.current = d; setItems(d); } })
      .catch(() => { if (alive) setItems([]); });
    return () => { alive = false; };
  }, [refreshKey]);

  const update = (idx, field, val) => {
    dirtyRef.current = true;
    itemsRef.current = itemsRef.current.map((x, i) => (i === idx ? { ...x, [field]: val } : x));
    setItems(itemsRef.current);
  };

  const addCard = () => {
    dirtyRef.current = true;
    const nc = { slug: '', title: '새 분야', subtitle: '', desc: '', imageKey: '', sort: itemsRef.current.length + 1, homeShow: false, _new: true };
    itemsRef.current = [...itemsRef.current, nc];
    setItems(itemsRef.current);
  };

  const sanitizeSlug = (v) => v.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '').replace(/-{2,}/g, '-').replace(/^-|-$/g, '');

  // 삭제 예약: 저장된 카드는 저장 시 DELETE, 미저장 카드는 로컬 제거(저장 전이면 되돌릴 수 있음)
  const delCard = (idx) => {
    const s = itemsRef.current[idx];
    if (!s._new && s.slug) {
      if (!window.confirm('이 카드를 삭제할까요? (저장 시 반영)')) return;
      delRef.current.push(s.slug);
    }
    dirtyRef.current = true;
    itemsRef.current = itemsRef.current.filter((_, i) => i !== idx);
    setItems(itemsRef.current);
  };

  // 드래그앤드롭 순서 변경 — sort 재부여(저장 시 DB 반영 → 홈페이지 정렬)
  const reorder = (from, to) => {
    dirtyRef.current = true;
    const arr = [...itemsRef.current];
    const [m] = arr.splice(from, 1);
    arr.splice(to, 0, m);
    itemsRef.current = arr.map((x, i) => ({ ...x, sort: i + 1 }));
    setItems(itemsRef.current);
  };
  const { containerRef, dragProps, dropProps, cardClass } = useDragReorder(reorder);

  const save = useCallback(async () => {
    const emptySlug = itemsRef.current.find((s) => s._new && !s.slug);
    if (emptySlug) throw new Error(`"${emptySlug.title}" 카드의 URL 슬러그를 입력해 주세요.`);
    const dupSlugs = itemsRef.current.map((s) => s.slug);
    const dup = dupSlugs.find((sl, i) => dupSlugs.indexOf(sl) !== i);
    if (dup) throw new Error(`슬러그 "${dup}"가 중복됩니다. 각 카드의 슬러그는 고유해야 합니다.`);
    for (const slug of delRef.current) {
      const res = await authFetch(`${API}/${encodeURIComponent(slug)}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(`솔루션 삭제 실패 (${res.status})`);
    }
    delRef.current = [];
    for (const s of itemsRef.current) {
      const res = await authFetch(API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          slug: s.slug, title: s.title, subtitle: s.subtitle, desc: s.desc,
          imageKey: s.imageKey, sort: s.sort, homeShow: s.homeShow,
        }),
      });
      if (!res.ok) throw new Error(`솔루션 저장 실패 (${res.status})`);
    }
    await saveSection();
    dirtyRef.current = false;
  }, [saveSection]);

  useEffect(() => {
    if (onRegisterSave) onRegisterSave(save);
    return () => { if (onRegisterSave) onRegisterSave(null); };
  }, [save, onRegisterSave]);

  const isDirty = useCallback(() => dirtyRef.current || uiIsDirty(), [uiIsDirty]);
  useEffect(() => {
    if (onRegisterDirty) onRegisterDirty(isDirty);
    return () => { if (onRegisterDirty) onRegisterDirty(null); };
  }, [isDirty, onRegisterDirty]);

  if (items === null) {
    return (
      <section className="section pt-page">
        <div className="container"><p className="heading-sub color-muted">불러오는 중…</p></div>
      </section>
    );
  }

  return (
    <>
      <section className="section pt-page">
        <div className="container">
          <div className="mb-2xl">
            <Editable as="h1" className="text-6xl" value={ui.title || '응용 분야'} onChange={(t) => setField('title', t)} />
            <Editable
              as="p"
              className="text-lg mt-xl"
              value={ui.desc || '국방, 항공우주, 철도, 통신, 산업 자동화를 위한 엔드투엔드 기술 솔루션 — 하드웨어 선정부터 시스템 통합, 라이프사이클 지원까지 아우릅니다.'}
              onChange={(t) => setField('desc', t)}
            />
          </div>
          <div className="svc-cards" ref={containerRef}>
            {items.map((s, i) => (
              <div className={`s-card ${cardClass(i)}`} key={s.slug} data-flip={s.slug} {...dragProps(i)} {...dropProps(i)}>
                <span className="drag-handle" title="카드를 드래그하여 순서 변경">⠿</span>
                <button type="button" className="slide-del-btn" title="카드 삭제" onClick={() => delCard(i)}>×</button>
                <ImageDrop value={s.imageKey} onChange={(k) => update(i, 'imageKey', k)} />
                <Editable as="h3" value={s.title} onChange={(t) => update(i, 'title', t)} />
                <Editable as="div" className="s-price" value={s.subtitle} onChange={(t) => update(i, 'subtitle', t)} />
                <Editable as="p" value={s.desc} onChange={(t) => update(i, 'desc', t)} />
                {s._new ? (
                  <div className="s-slug-row">
                    <label className="s-slug-label">URL 슬러그</label>
                    <input
                      className="s-slug-input"
                      value={s.slug}
                      placeholder="예: telecom, defense"
                      onChange={(e) => update(i, 'slug', sanitizeSlug(e.target.value))}
                    />
                    <small className="s-slug-preview">/products/<strong>{s.slug || '…'}</strong></small>
                  </div>
                ) : (
                  <small className="s-slug-preview">/products/{s.slug}</small>
                )}
                <button type="button" className="card-edit-btn"
                  disabled={s._new && !s.slug}
                  onClick={() => onOpenProduct && onOpenProduct(s.slug)}>
                  {CUSTOM_PROD_SLUGS.has(s.slug) ? '제품 카탈로그 수정 →' : '제품 수정 →'}
                </button>
              </div>
            ))}
            <button type="button" className="card-add-tile" onClick={addCard}>+ 카드 추가</button>
          </div>
        </div>
      </section>

      <section className="contact-bright section">
        <div className="container">
          <div className="contact-grid">
            <div className="contact-txt">
              <Editable as="h2" value="견적 요청" />
              <Editable as="p" value="프로젝트 요구사항을 알려주시면, 당사 엔지니어링 팀이 영업일 기준 2일 이내에 맞춤형 솔루션 제안을 드립니다." />
            </div>
            <ContactFormPreview />
          </div>
        </div>
      </section>
    </>
  );
}
